/**
 * IosNotificationService — détection d'environnement iOS / iPadOS pour les
 * notifications PWA.
 *
 * Ce service ne fait QUE de la détection (UA, mode standalone, version iOS).
 * Toute la logique de demande de permission est désormais centralisée dans
 * `PushNotificationService.subscribeToNotifications()` (ou `WebPushService`
 * pour les nouvelles pages), qui invoquent directement
 * `Notification.requestPermission()` — seul appel qui déclenche le prompt
 * natif et fait apparaître l'app dans Réglages > Notifications.
 *
 * Historique : ce service contenait précédemment plusieurs guides affichés
 * via `MatSnackBar.open(htmlString, …)`. Or `MatSnackBar` n'interprète pas
 * le HTML : l'utilisateur voyait du code brut à l'écran. Cette logique a
 * été retirée. Les composants appelants se contentent maintenant d'un
 * toast simple ou d'un message contextuel.
 */
import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class IosNotificationService {
  private isIOS = false;
  private isPWA = false;
  /** Version iOS majeure (entier) — 0 si non iOS / non détectable. */
  private iosVersion = 0;

  constructor() {
    this.detectIOSEnvironment();
  }

  private detectIOSEnvironment(): void {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
    const ua = navigator.userAgent.toLowerCase();

    // Détection iPhone / iPad / iPod, plus iPad « desktop class » qui se
    // déclare en MacIntel mais expose maxTouchPoints > 1.
    this.isIOS =
      /iphone|ipad|ipod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Mode PWA installée (Add to Home Screen) ou standalone Chrome Android.
    this.isPWA =
      window.matchMedia?.('(display-mode: standalone)').matches === true ||
      (window.navigator as any).standalone === true;

    if (this.isIOS) {
      const m = ua.match(/os (\d+)_/);
      if (m) this.iosVersion = parseInt(m[1], 10);
    }
  }

  /**
   * Statique de l'environnement courant — utilisé par les composants UI
   * pour adapter leur copy (ex : "installez d'abord l'app" sur iOS Safari
   * non-PWA, ou "iOS 16.4 requis").
   */
  getEnvironmentInfo(): {
    isIOS: boolean;
    isPWA: boolean;
    iosVersion: number;
    notificationsAvailable: boolean;
    requiresInstallation: boolean;
  } {
    // iOS 16.4+ supporte les Web Push, mais uniquement quand la PWA est
    // installée sur l'écran d'accueil. iOS < 16.4 n'a pas de Web Push.
    const notificationsAvailable = !this.isIOS
      ? typeof Notification !== 'undefined'
      : this.iosVersion >= 16 && this.isPWA;

    return {
      isIOS: this.isIOS,
      isPWA: this.isPWA,
      iosVersion: this.iosVersion,
      notificationsAvailable,
      requiresInstallation: this.isIOS && !this.isPWA,
    };
  }

  /** True si le device courant peut probablement recevoir des notifications. */
  canUseNotifications(): boolean {
    return this.getEnvironmentInfo().notificationsAvailable;
  }
}
